import React, { useState, useRef, useCallback } from 'react';
import { colors, fonts, spacing, radii, shadows, transitions } from '../styles/theme';
import { submitFeedback } from '../services/api';

// ─── Types & Constants ─────────────────────────────────────────────────

interface FeedbackQuestion {
    id: string;
    title: string;
    subtitle?: string;
    type: 'affect-grid' | 'radar' | 'triple' | 'nps' | 'tags' | 'text';
    options?: string[];
    optionEmojis?: string[];
    textFields?: Array<{ id: string; placeholder: string }>;
    hasComment?: boolean;
    commentPlaceholder?: string;
    spriteLeft: string;
    spriteRight: string;
}

const RADAR_DIMENSIONS = [
    { id: 'clarity', label: 'Clarity' },
    { id: 'relevance', label: 'Relevance' },
    { id: 'completeness', label: 'Completeness' },
    { id: 'navigation', label: 'Navigation' },
    { id: 'visual_design', label: 'Visual Design' },
    { id: 'engagement', label: 'Engagement' },
];

const QUESTIONS: FeedbackQuestion[] = [
    {
        id: 'affect_grid',
        title: 'How did the tool make you feel overall?',
        subtitle: 'Place your marker on the grid — position captures both your emotional reaction and engagement level',
        type: 'affect-grid',
        spriteLeft: '',
        spriteRight: '',
    },
    {
        id: 'radar',
        title: 'Rate these aspects of the tool',
        subtitle: 'Click along each axis to set your rating (1–5)',
        type: 'radar',
        hasComment: true,
        commentPlaceholder: 'Spotted any gaps in our coverage? (optional)',
        spriteLeft: '',
        spriteRight: '',
    },
    {
        id: 'depth',
        title: 'Was the level of detail appropriate?',
        type: 'triple',
        options: ['Too shallow', 'Just right', 'Too deep'],
        spriteLeft: '',
        spriteRight: '',
    },
    {
        id: 'shopping_metaphor',
        title: 'Did the "shopping bag" concept make sense?',
        type: 'triple',
        options: ['Not really', 'Somewhat', 'Yes, definitely'],
        spriteLeft: '',
        spriteRight: '',
    },
    {
        id: 'session_length',
        title: 'How was the assessment length?',
        type: 'triple',
        options: ['Too short', 'Just right', 'Too long'],
        spriteLeft: '',
        spriteRight: '',
    },
    {
        id: 'nps_score',
        title: 'How likely are you to recommend this to a colleague?',
        subtitle: '0 = not at all · 10 = extremely likely',
        type: 'nps',
        spriteLeft: '',
        spriteRight: '',
    },
    {
        id: 'text_feedback',
        title: 'Anything else you want to tell us?',
        type: 'text',
        textFields: [
            { id: 'improvements', placeholder: 'What would you improve?' },
            { id: 'general_comments', placeholder: 'Any other thoughts, ideas, or observations?' },
        ],
        spriteLeft: '',
        spriteRight: '',
    },
];

const TOTAL_STEPS = QUESTIONS.length + 2;

// ─── 2D Affect Grid (Russell 1989; Toet et al. 2018) ───────────────────
// X = Valence (unpleasant → pleasant), Y = Arousal (calm → activated)

const AffectGrid: React.FC<{
    value: { x: number; y: number } | null;
    onChange: (v: { x: number; y: number }) => void;
}> = ({ value, onChange }) => {
    const gridRef = useRef<HTMLDivElement>(null);
    const [dragging, setDragging] = useState(false);

    const updateFromEvent = useCallback((clientX: number, clientY: number) => {
        if (!gridRef.current) return;
        const rect = gridRef.current.getBoundingClientRect();
        const x = Math.max(0, Math.min(100, Math.round(((clientX - rect.left) / rect.width) * 100)));
        const y = Math.max(0, Math.min(100, Math.round((1 - (clientY - rect.top) / rect.height) * 100)));
        onChange({ x, y });
    }, [onChange]);

    const handlePointerDown = useCallback((e: React.PointerEvent) => {
        setDragging(true);
        (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
        updateFromEvent(e.clientX, e.clientY);
    }, [updateFromEvent]);

    const handlePointerMove = useCallback((e: React.PointerEvent) => {
        if (!dragging) return;
        updateFromEvent(e.clientX, e.clientY);
    }, [dragging, updateFromEvent]);

    const handlePointerUp = useCallback(() => setDragging(false), []);

    const getQuadrantDesc = (x: number, y: number) => {
        if (x >= 50 && y >= 50) return 'Excited & positive';
        if (x < 50 && y >= 50) return 'Tense & negative';
        if (x >= 50 && y < 50) return 'Calm & positive';
        return 'Bored & negative';
    };

    return (
        <div style={sub.gridOuter}>
            <div style={sub.gridRow}>
                {/* Y-axis label (rotated) */}
                <div style={sub.yAxisLabelCol}>
                    <span style={sub.yAxisTop}>Activated ↑</span>
                    <span style={sub.yAxisBottom}>↓ Calm</span>
                </div>

                {/* The clickable grid */}
                <div style={sub.gridWrapper}>
                    <div
                        ref={gridRef}
                        style={sub.gridInner}
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        onPointerLeave={handlePointerUp}
                    >
                        {/* Quadrant tints */}
                        <div style={sub.quadrantGrid}>
                            <div style={{ ...sub.quadrant, background: 'radial-gradient(ellipse at bottom right, #d4915c0c, transparent 70%)' }} />
                            <div style={{ ...sub.quadrant, background: 'radial-gradient(ellipse at bottom left, #5a9e6f0f, transparent 70%)' }} />
                            <div style={{ ...sub.quadrant, background: 'radial-gradient(ellipse at top right, #c95d5d0c, transparent 70%)' }} />
                            <div style={{ ...sub.quadrant, background: 'radial-gradient(ellipse at top left, #7ba3c90c, transparent 70%)' }} />
                        </div>

                        {/* Grid lines */}
                        <div style={{ ...sub.gridLine, left: '50%', top: 0, bottom: 0, width: 1 }} />
                        <div style={{ ...sub.gridLine, top: '50%', left: 0, right: 0, height: 1 }} />
                        <div style={{ ...sub.gridLineMinor, left: '25%', top: 0, bottom: 0, width: 1 }} />
                        <div style={{ ...sub.gridLineMinor, left: '75%', top: 0, bottom: 0, width: 1 }} />
                        <div style={{ ...sub.gridLineMinor, top: '25%', left: 0, bottom: 0, height: 1 }} />
                        <div style={{ ...sub.gridLineMinor, top: '75%', left: 0, right: 0, height: 1 }} />

                        {/* Marker */}
                        {value && (
                            <div
                                style={{
                                    position: 'absolute',
                                    left: `${value.x}%`,
                                    bottom: `${value.y}%`,
                                    transform: 'translate(-50%, 50%)',
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    backgroundColor: colors.primary,
                                    border: '3px solid white',
                                    boxShadow: `0 2px 10px rgba(26,107,58,0.4), 0 0 0 ${dragging ? '8px' : '4px'} ${colors.primary}22`,
                                    transition: dragging ? 'none' : 'box-shadow 200ms ease',
                                    zIndex: 10,
                                    pointerEvents: 'none',
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* X-axis text */}
            <div style={sub.xAxisRow}>
                <span style={sub.axisEndLabel}>← Unpleasant</span>
                <span style={sub.axisEndLabel}>Pleasant →</span>
            </div>

            {/* Position readout */}
            <div style={sub.readout}>
                {value ? getQuadrantDesc(value.x, value.y) : 'Click or drag anywhere on the grid'}
            </div>
        </div>
    );
};

// ─── Interactive Radar Chart (SVG) ─────────────────────────────────────

const RADAR_SIZE = 380;
const RADAR_CX = RADAR_SIZE / 2;
const RADAR_CY = RADAR_SIZE / 2;
const RADAR_R = 130;
const RADAR_LEVELS = 5;
const RADAR_N = RADAR_DIMENSIONS.length;

function radarPoint(dimIdx: number, level: number) {
    const angle = (2 * Math.PI / RADAR_N) * dimIdx - Math.PI / 2;
    return {
        x: RADAR_CX + (level / RADAR_LEVELS) * RADAR_R * Math.cos(angle),
        y: RADAR_CY + (level / RADAR_LEVELS) * RADAR_R * Math.sin(angle),
    };
}

function hexagonPoints(level: number) {
    return RADAR_DIMENSIONS.map((_, i) => {
        const pt = radarPoint(i, level);
        return `${pt.x},${pt.y}`;
    }).join(' ');
}

const RadarChart: React.FC<{
    values: Record<string, number>;
    onChange: (dimId: string, value: number) => void;
}> = ({ values, onChange }) => {
    const ratedCount = RADAR_DIMENSIONS.filter(d => values[d.id] > 0).length;

    const valuePolygon = RADAR_DIMENSIONS.map((dim, i) => {
        const val = values[dim.id] || 0;
        const pt = radarPoint(i, val);
        return `${pt.x},${pt.y}`;
    }).join(' ');

    const hasAnyValue = ratedCount > 0;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <svg
                viewBox={`0 0 ${RADAR_SIZE} ${RADAR_SIZE}`}
                style={{ width: '100%', maxWidth: 380, height: 'auto' }}
            >
                {/* Grid rings */}
                {[1, 2, 3, 4, 5].map(level => (
                    <polygon
                        key={level}
                        points={hexagonPoints(level)}
                        fill="none"
                        stroke={colors.border}
                        strokeWidth={level === 5 ? 1.5 : 0.7}
                        strokeDasharray={level === 5 ? 'none' : '3,3'}
                    />
                ))}

                {/* Axis lines */}
                {RADAR_DIMENSIONS.map((_, i) => {
                    const pt = radarPoint(i, RADAR_LEVELS);
                    return (
                        <line
                            key={i}
                            x1={RADAR_CX} y1={RADAR_CY}
                            x2={pt.x} y2={pt.y}
                            stroke={colors.border}
                            strokeWidth={0.7}
                        />
                    );
                })}

                {/* Level labels along first axis */}
                {[1, 2, 3, 4, 5].map(level => {
                    const pt = radarPoint(0, level);
                    return (
                        <text
                            key={level}
                            x={pt.x + 8} y={pt.y - 4}
                            fontSize={9}
                            fill={colors.textMuted}
                            fontFamily="Nunito, sans-serif"
                        >
                            {level}
                        </text>
                    );
                })}

                {/* Value polygon */}
                {hasAnyValue && (
                    <polygon
                        points={valuePolygon}
                        fill={`${colors.primary}20`}
                        stroke={colors.primary}
                        strokeWidth={2.5}
                        strokeLinejoin="round"
                    />
                )}

                {/* Ghost dots (all clickable positions) */}
                {RADAR_DIMENSIONS.map((dim, i) =>
                    [1, 2, 3, 4, 5].map(level => {
                        const pt = radarPoint(i, level);
                        const isActive = values[dim.id] === level;
                        const isOnPath = values[dim.id] > 0 && values[dim.id] >= level;
                        return (
                            <circle
                                key={`${dim.id}-${level}`}
                                cx={pt.x} cy={pt.y}
                                r={isActive ? 11 : 7}
                                fill={isActive ? colors.primary : isOnPath ? `${colors.primary}50` : colors.bgWarm}
                                stroke={isActive ? 'white' : isOnPath ? colors.primary : colors.border}
                                strokeWidth={isActive ? 2.5 : 1.5}
                                style={{ cursor: 'pointer', transition: 'all 200ms ease' }}
                                onClick={() => onChange(dim.id, level)}
                            />
                        );
                    })
                )}

                {/* Dimension labels */}
                {RADAR_DIMENSIONS.map((dim, i) => {
                    const angle = (2 * Math.PI / RADAR_N) * i - Math.PI / 2;
                    const labelR = RADAR_R + 32;
                    const x = RADAR_CX + labelR * Math.cos(angle);
                    const y = RADAR_CY + labelR * Math.sin(angle);
                    const rated = values[dim.id] > 0;
                    return (
                        <text
                            key={dim.id}
                            x={x} y={y}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize={12.5}
                            fontWeight={rated ? 700 : 400}
                            fill={rated ? colors.text : colors.textMuted}
                            fontFamily="Nunito, sans-serif"
                            style={{ transition: 'all 200ms ease' }}
                        >
                            {dim.label}
                            {rated && (
                                <tspan fontSize={10} fill={colors.primary}>
                                    {' '}{values[dim.id]}
                                </tspan>
                            )}
                        </text>
                    );
                })}
            </svg>

            <p style={{
                fontSize: fonts.xs,
                color: ratedCount === RADAR_N ? colors.yes : colors.textMuted,
                marginTop: spacing[1],
                fontWeight: ratedCount === RADAR_N ? (fonts.semibold as number) : (fonts.normal as number),
            }}>
                {ratedCount === RADAR_N
                    ? '✓ All dimensions rated'
                    : `${ratedCount} of ${RADAR_N} rated — click the dots on each axis`}
            </p>
        </div>
    );
};

// ─── Simple Components (kept as-is) ────────────────────────────────────

const TripleChoice: React.FC<{
    value: number | null;
    onChange: (v: number) => void;
    options: string[];
    optionEmojis?: string[];
}> = ({ value, onChange, options, optionEmojis }) => {
    const tripleColors = [colors.no, colors.yes, colors.cart];
    return (
        <div style={sub.tripleRow}>
            {options.map((opt, idx) => {
                const val = idx + 1;
                const selected = value === val;
                const col = tripleColors[idx];
                return (
                    <button
                        key={val}
                        type="button"
                        onClick={() => onChange(val)}
                        style={{
                            ...sub.tripleButton,
                            backgroundColor: selected ? col : colors.white,
                            color: selected ? colors.white : colors.text,
                            borderColor: selected ? col : colors.border,
                            transform: selected ? 'scale(1.05)' : 'scale(1)',
                            boxShadow: selected ? `0 4px 16px ${col}40` : shadows.sm,
                        }}
                    >
                        {optionEmojis && (
                            <span style={{ fontSize: '1.8rem', marginBottom: 4 }}>
                                {optionEmojis[idx]}
                            </span>
                        )}
                        <span style={{ fontSize: fonts.sm, fontWeight: fonts.semibold as number }}>
                            {opt}
                        </span>
                    </button>
                );
            })}
        </div>
    );
};

const NpsScale: React.FC<{
    value: number | null;
    onChange: (v: number) => void;
}> = ({ value, onChange }) => {
    const getNpsColor = (n: number) => {
        if (n <= 6) return colors.no;
        if (n <= 8) return colors.cart;
        return colors.yes;
    };
    return (
        <div>
            <div style={sub.npsRow}>
                {Array.from({ length: 11 }, (_, i) => {
                    const selected = value === i;
                    const col = getNpsColor(i);
                    return (
                        <button
                            key={i}
                            type="button"
                            onClick={() => onChange(i)}
                            style={{
                                ...sub.npsButton,
                                backgroundColor: selected ? col : colors.white,
                                color: selected ? colors.white : colors.textSecondary,
                                borderColor: selected ? col : colors.border,
                                transform: selected ? 'scale(1.2)' : 'scale(1)',
                                boxShadow: selected ? `0 2px 8px ${col}40` : 'none',
                                fontWeight: selected ? (fonts.bold as number) : (fonts.medium as number),
                            }}
                        >
                            {i}
                        </button>
                    );
                })}
            </div>
            <div style={sub.npsLabels}>
                <span style={{ color: colors.textMuted, fontSize: fonts.xs }}>Not likely</span>
                <span style={{ color: colors.textMuted, fontSize: fonts.xs }}>Very likely</span>
            </div>
        </div>
    );
};

const TagPicker: React.FC<{
    value: string[];
    onChange: (v: string[]) => void;
    options: string[];
}> = ({ value, onChange, options }) => {
    const toggle = (tag: string) => {
        onChange(value.includes(tag) ? value.filter(t => t !== tag) : [...value, tag]);
    };
    return (
        <div style={sub.tagGrid}>
            {options.map(tag => {
                const selected = value.includes(tag);
                return (
                    <button
                        key={tag}
                        type="button"
                        onClick={() => toggle(tag)}
                        style={{
                            ...sub.tagButton,
                            backgroundColor: selected ? colors.primary : colors.white,
                            color: selected ? colors.white : colors.textSecondary,
                            borderColor: selected ? colors.primary : colors.border,
                            transform: selected ? 'scale(1.05)' : 'scale(1)',
                        }}
                    >
                        {selected ? '✓ ' : ''}{tag}
                    </button>
                );
            })}
        </div>
    );
};

const TextFields: React.FC<{
    values: Record<string, string>;
    onChange: (id: string, v: string) => void;
    fields: Array<{ id: string; placeholder: string }>;
}> = ({ values, onChange, fields }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[4] }}>
        {fields.map(f => (
            <textarea
                key={f.id}
                value={values[f.id] || ''}
                onChange={e => onChange(f.id, e.target.value)}
                placeholder={f.placeholder}
                rows={4}
                style={sub.textarea}
            />
        ))}
    </div>
);

// ─── Main Component ────────────────────────────────────────────────────

const ExpertFeedback: React.FC = () => {
    const [step, setStep] = useState(0);
    const [responses, setResponses] = useState<Record<string, any>>({
        radar: {} as Record<string, number>,
    });
    const [stepComments, setStepComments] = useState<Record<string, string>>({});
    const [commentOpen, setCommentOpen] = useState<Record<string, boolean>>({});
    const [expertName, setExpertName] = useState('');
    const [expertEmail, setExpertEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [fadeState, setFadeState] = useState<'in' | 'out'>('in');

    const progress = (step / (TOTAL_STEPS - 1)) * 100;
    const isWelcome = step === 0;
    const isThankYou = step === TOTAL_STEPS - 1;
    const currentQuestion = !isWelcome && !isThankYou ? QUESTIONS[step - 1] : null;
    const isLastQuestion = step === TOTAL_STEPS - 2;

    const handleResponse = (id: string, value: any) => {
        setResponses(prev => ({ ...prev, [id]: value }));
    };

    const animateToStep = (nextStep: number) => {
        setFadeState('out');
        setTimeout(() => {
            setStep(nextStep);
            setFadeState('in');
        }, 180);
    };

    const handleNext = () => animateToStep(step + 1);
    const handleBack = () => animateToStep(Math.max(0, step - 1));

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setSubmitError('');
        try {
            const grid = responses.affect_grid || {};
            const radar = responses.radar || {};
            const textResponses = responses.text_feedback || {};

            const inlineNotes = Object.entries(stepComments)
                .filter(([, v]) => v.trim())
                .map(([qId, v]) => {
                    const q = QUESTIONS.find(q => q.id === qId);
                    return `[${q?.title || qId}] ${v.trim()}`;
                })
                .join('\n');
            const generalRaw = textResponses.general_comments || '';
            const combinedComments = [inlineNotes, generalRaw].filter(Boolean).join('\n---\n');

            await submitFeedback({
                expertName,
                expertEmail,
                overall_valence: grid.x ?? null,
                overall_arousal: grid.y ?? null,
                clarity: radar.clarity ?? null,
                relevance: radar.relevance ?? null,
                completeness: radar.completeness ?? null,
                completeness_comment: responses.completeness_comment || '',
                navigation: radar.navigation ?? null,
                visual_design: radar.visual_design ?? null,
                engagement: radar.engagement ?? null,
                depth: responses.depth ?? null,
                shopping_metaphor: responses.shopping_metaphor ?? null,
                session_length: responses.session_length ?? null,
                nps_score: responses.nps_score ?? null,
                top_strengths: responses.top_strengths || [],
                improvements: textResponses.improvements || '',
                general_comments: combinedComments,
            });
            animateToStep(TOTAL_STEPS - 1);
        } catch {
            setSubmitError('Something went wrong. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const canProceed = () => {
        if (isWelcome) return true;
        if (isThankYou) return false;
        if (!currentQuestion) return false;
        if (currentQuestion.type === 'text') return true;
        if (currentQuestion.type === 'tags') return true;
        if (currentQuestion.type === 'affect-grid') return responses.affect_grid != null;
        if (currentQuestion.type === 'radar') {
            const radar = responses.radar || {};
            return RADAR_DIMENSIONS.every(d => radar[d.id] > 0);
        }
        return responses[currentQuestion.id] != null;
    };

    return (
        <div style={styles.page}>
            <div style={styles.progressStrip}>
                <div style={styles.progressTrack}>
                    <div style={{ ...styles.progressFill, width: `${progress}%` }} />
                </div>
            </div>

            <div style={styles.container}>
                <div
                    style={{
                        ...styles.content,
                        opacity: fadeState === 'in' ? 1 : 0,
                        transform: fadeState === 'in' ? 'translateY(0)' : 'translateY(10px)',
                        transition: 'opacity 180ms ease, transform 180ms ease',
                    }}
                >

                    {/* ── Welcome ──────────────────────────────────── */}
                    {isWelcome && (
                        <div style={styles.card}>
                            <h1 style={styles.cardTitle}>Feedback</h1>
                            <p style={styles.cardSubtitle}>
                                Help us make the Shopping Bag Tool better! This takes about <strong>5 minutes</strong> — mostly visual interactions.
                            </p>
                            <div style={styles.fieldGroup}>
                                <div style={styles.field}>
                                    <label htmlFor="fb-name" style={styles.label}>Your name</label>
                                    <input id="fb-name" type="text" value={expertName}
                                        onChange={e => setExpertName(e.target.value)}
                                        placeholder="How should we call you?" style={styles.input} />
                                </div>
                                <div style={styles.field}>
                                    <label htmlFor="fb-email" style={styles.label}>Email (optional, for follow-up)</label>
                                    <input id="fb-email" type="email" value={expertEmail}
                                        onChange={e => setExpertEmail(e.target.value)}
                                        placeholder="you@example.com" style={styles.input} />
                                </div>
                            </div>
                            <button type="button" style={styles.primaryButton} onClick={handleNext}>
                                Continue
                            </button>
                            <p style={styles.disclaimer}>
                                Your feedback is stored securely and only visible to the tool's creators.
                            </p>
                        </div>
                    )}

                    {/* ── Question Steps ───────────────────────────── */}
                    {currentQuestion && (
                        <div style={styles.card}>
                            <h2 style={styles.cardTitle}>{currentQuestion.title}</h2>
                            {currentQuestion.subtitle && (
                                <p style={styles.cardSubtitle}>{currentQuestion.subtitle}</p>
                            )}

                            <div style={styles.inputArea}>
                                {currentQuestion.type === 'affect-grid' && (
                                    <AffectGrid
                                        value={responses.affect_grid ?? null}
                                        onChange={v => handleResponse('affect_grid', v)}
                                    />
                                )}
                                {currentQuestion.type === 'radar' && (
                                    <RadarChart
                                        values={responses.radar || {}}
                                        onChange={(dimId, val) =>
                                            handleResponse('radar', { ...(responses.radar || {}), [dimId]: val })
                                        }
                                    />
                                )}
                                {currentQuestion.type === 'triple' && (
                                    <TripleChoice
                                        value={responses[currentQuestion.id] ?? null}
                                        onChange={v => handleResponse(currentQuestion.id, v)}
                                        options={currentQuestion.options!}
                                        optionEmojis={currentQuestion.optionEmojis}
                                    />
                                )}
                                {currentQuestion.type === 'nps' && (
                                    <NpsScale
                                        value={responses[currentQuestion.id] ?? null}
                                        onChange={v => handleResponse(currentQuestion.id, v)}
                                    />
                                )}
                                {currentQuestion.type === 'text' && (
                                    <TextFields
                                        values={responses[currentQuestion.id] || {}}
                                        onChange={(fid, v) =>
                                            handleResponse(currentQuestion.id, {
                                                ...(responses[currentQuestion.id] || {}),
                                                [fid]: v,
                                            })
                                        }
                                        fields={currentQuestion.textFields!}
                                    />
                                )}
                            </div>

                            {currentQuestion.hasComment && (
                                <textarea
                                    value={responses.completeness_comment || ''}
                                    onChange={e => handleResponse('completeness_comment', e.target.value)}
                                    placeholder={currentQuestion.commentPlaceholder}
                                    rows={2}
                                    style={{ ...sub.textarea, marginTop: spacing[4] }}
                                />
                            )}

                            {/* Collapsible short comment on every question */}
                            {currentQuestion.type !== 'text' && (
                                <div style={{ marginTop: spacing[3] }}>
                                    <button
                                        type="button"
                                        onClick={() => setCommentOpen(prev => ({
                                            ...prev,
                                            [currentQuestion.id]: !prev[currentQuestion.id],
                                        }))}
                                        style={styles.commentToggle}
                                    >
                                        {commentOpen[currentQuestion.id] ? '− Hide comment' : '+ Add a short comment'}
                                    </button>
                                    {commentOpen[currentQuestion.id] && (
                                        <textarea
                                            value={stepComments[currentQuestion.id] || ''}
                                            onChange={e => setStepComments(prev => ({
                                                ...prev,
                                                [currentQuestion.id]: e.target.value,
                                            }))}
                                            placeholder="Optional comment for this question..."
                                            rows={2}
                                            style={{ ...sub.textarea, marginTop: spacing[2] }}
                                        />
                                    )}
                                </div>
                            )}

                            <div style={styles.navRow}>
                                <button type="button" style={styles.backButton} onClick={handleBack}>
                                    ← Back
                                </button>
                                {isLastQuestion ? (
                                    <button
                                        type="button"
                                        style={{ ...styles.primaryButton, opacity: isSubmitting ? 0.6 : 1 }}
                                        onClick={handleSubmit}
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Submitting...' : 'Submit feedback'}
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        style={{
                                            ...styles.primaryButton,
                                            ...(canProceed() ? {} : styles.primaryButtonDisabled),
                                        }}
                                        onClick={handleNext}
                                        disabled={!canProceed()}
                                    >
                                        Continue
                                    </button>
                                )}
                            </div>
                            {submitError && <p style={styles.errorText}>{submitError}</p>}
                        </div>
                    )}

                    {/* ── Thank You ────────────────────────────────── */}
                    {isThankYou && (
                        <div style={styles.card}>
                            <div style={{ textAlign: 'center' as const }}>
                                <h1 style={{ ...styles.cardTitle, marginTop: spacing[3] }}>Thank you for your feedback</h1>
                            </div>

                            <div style={styles.contactCardsRow}>
                                <div style={styles.personCard}>
                                    <img
                                        src={`${process.env.PUBLIC_URL || ''}/image.png`}
                                        alt="Laurynas Adomaitis"
                                        style={styles.personImage}
                                    />
                                    <h3 style={styles.personName}>Laurynas Adomaitis</h3>
                                    <p style={styles.personRole}>Ethics researcher, RISE</p>
                                    <div style={styles.personLinks}>
                                        <a href="https://www.linkedin.com/in/laurynas-adomaitis/" target="_blank" rel="noreferrer" style={styles.linkButton}>LinkedIn</a>
                                        <a href="mailto:laurynas.adomaitis@ri.se?subject=Shopping%20Bag%20Tool" style={styles.linkSecondaryButton}>Email</a>
                                    </div>
                                </div>
                                <div style={styles.personCard}>
                                    <img
                                        src={`${process.env.PUBLIC_URL || ''}/image%20copy.png`}
                                        alt="Emmie Nordell"
                                        style={styles.personImage}
                                    />
                                    <h3 style={styles.personName}>Emmie Nordell</h3>
                                    <p style={styles.personRole}>Legal researcher, RISE</p>
                                    <div style={styles.personLinks}>
                                        <a href="https://www.linkedin.com/in/emmie-nordell-16154b60/" target="_blank" rel="noreferrer" style={styles.linkButton}>LinkedIn</a>
                                        <a href="mailto:emmie.nordell@ri.se?subject=Shopping%20Bag%20Tool" style={styles.linkSecondaryButton}>Email</a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {!isWelcome && !isThankYou && (
                        <p style={styles.stepIndicator}>{step} of {QUESTIONS.length}</p>
                    )}
                </div>
            </div>
        </div>
    );
};

// ─── Sub-component Styles ──────────────────────────────────────────────

const sub: Record<string, React.CSSProperties> = {
    // Affect Grid
    gridOuter: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    gridRow: {
        display: 'flex',
        alignItems: 'stretch',
        gap: 8,
        width: '100%',
        maxWidth: 380,
    },
    yAxisLabelCol: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0,
        paddingTop: 2,
        paddingBottom: 2,
    },
    yAxisTop: {
        fontSize: fonts.xs,
        fontWeight: fonts.semibold as number,
        color: colors.textSecondary,
        writingMode: 'vertical-rl' as any,
        textOrientation: 'mixed' as any,
        letterSpacing: '0.04em',
    },
    yAxisBottom: {
        fontSize: fonts.xs,
        fontWeight: fonts.semibold as number,
        color: colors.textSecondary,
        writingMode: 'vertical-rl' as any,
        textOrientation: 'mixed' as any,
        letterSpacing: '0.04em',
    },
    gridWrapper: {
        position: 'relative',
        width: '100%',
        paddingBottom: '100%',
        flexGrow: 1,
    },
    gridInner: {
        position: 'absolute',
        inset: 0,
        borderRadius: radii.lg,
        border: `2px solid ${colors.border}`,
        overflow: 'hidden',
        cursor: 'crosshair',
        touchAction: 'none',
        backgroundColor: colors.bgLight,
    },
    quadrantGrid: {
        position: 'absolute',
        inset: 0,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: '1fr 1fr',
    },
    quadrant: {
        width: '100%',
        height: '100%',
    },
    gridLine: {
        position: 'absolute',
        backgroundColor: `${colors.border}`,
    },
    gridLineMinor: {
        position: 'absolute',
        backgroundColor: `${colors.borderLight}`,
    },
    xAxisRow: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        maxWidth: 380,
        marginTop: 6,
    },
    axisEndLabel: {
        fontSize: fonts.xs,
        fontWeight: fonts.semibold as number,
        color: colors.textSecondary,
    },
    readout: {
        marginTop: spacing[2],
        fontSize: fonts.sm,
        fontWeight: fonts.medium as number,
        color: colors.primary,
        textAlign: 'center' as const,
        minHeight: 22,
    },
    // Triple
    tripleRow: {
        display: 'flex',
        gap: spacing[3],
        justifyContent: 'center',
        flexWrap: 'wrap',
    },
    tripleButton: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${spacing[4]} ${spacing[5]}`,
        border: '2px solid',
        borderRadius: radii.xl,
        cursor: 'pointer',
        transition: `all ${transitions.normal}`,
        minWidth: 120,
        minHeight: 90,
        background: colors.white,
    },
    // NPS
    npsRow: {
        display: 'flex',
        justifyContent: 'center',
        gap: spacing[1],
        flexWrap: 'wrap',
    },
    npsButton: {
        width: 40, height: 40,
        borderRadius: '50%',
        border: '2px solid',
        cursor: 'pointer',
        fontSize: fonts.sm,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: `all ${transitions.normal}`,
        padding: 0,
    },
    npsLabels: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: spacing[2],
        padding: `0 ${spacing[1]}`,
    },
    // Tags
    tagGrid: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: spacing[2],
        justifyContent: 'center',
    },
    tagButton: {
        padding: `${spacing[2]} ${spacing[4]}`,
        borderRadius: radii.full,
        border: '2px solid',
        cursor: 'pointer',
        fontSize: fonts.sm,
        fontWeight: fonts.medium as number,
        transition: `all ${transitions.normal}`,
        whiteSpace: 'nowrap' as const,
    },
    // Text
    textarea: {
        width: '100%',
        padding: spacing[3],
        fontSize: fonts.base,
        fontFamily: 'inherit',
        borderRadius: radii.lg,
        border: `1px solid ${colors.border}`,
        outline: 'none',
        resize: 'vertical' as const,
        backgroundColor: colors.white,
        transition: `border-color ${transitions.fast}`,
        minHeight: 80,
    },
};

// ─── Main Styles ───────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
    page: {
        minHeight: '100vh',
        backgroundColor: colors.bgLight,
        display: 'flex',
        flexDirection: 'column',
    },
    progressStrip: {
        backgroundColor: colors.white,
        borderBottom: `1px solid ${colors.border}`,
        padding: `${spacing[3]} ${spacing[6]}`,
    },
    progressTrack: {
        maxWidth: '720px',
        margin: '0 auto',
        height: 8,
        backgroundColor: colors.bgWarm,
        borderRadius: '9999px',
        overflow: 'hidden' as const,
    },
    progressFill: {
        height: '100%',
        backgroundColor: colors.yes,
        borderRadius: '9999px',
        transition: 'width 0.4s ease',
    },
    container: {
        flex: 1,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: `${spacing[6]} ${spacing[4]}`,
        paddingTop: spacing[4],
    },
    content: { width: '100%', maxWidth: '680px' },
    spriteRow: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: spacing[2],
    },
    card: {
        backgroundColor: colors.white,
        borderRadius: radii.xl,
        border: `1px solid ${colors.border}`,
        padding: `${spacing[6]} ${spacing[8]}`,
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
    },
    cardTitle: {
        fontSize: fonts['2xl'],
        fontWeight: fonts.bold as number,
        color: colors.text,
        marginBottom: spacing[2],
    },
    cardSubtitle: {
        fontSize: fonts.base,
        color: colors.textSecondary,
        marginBottom: spacing[5],
        lineHeight: fonts.lineHeightRelaxed,
    },
    inputArea: { marginBottom: spacing[6] },
    fieldGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[4],
        marginBottom: spacing[6],
    },
    field: { display: 'flex', flexDirection: 'column', gap: spacing[1] },
    label: { fontSize: fonts.sm, fontWeight: fonts.medium as number, color: colors.text },
    input: {
        padding: `${spacing[3]} ${spacing[3]}`,
        fontSize: fonts.base,
        borderRadius: radii.md,
        border: `1px solid ${colors.border}`,
        outline: 'none',
        fontFamily: 'inherit',
    },
    primaryButton: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${spacing[4]} ${spacing[6]}`,
        fontSize: fonts.lg,
        fontWeight: fonts.bold as number,
        color: colors.white,
        backgroundColor: colors.primary,
        border: 'none',
        borderRadius: radii.lg,
        cursor: 'pointer',
        minHeight: '44px',
        boxShadow: 'none',
        transition: `all ${transitions.fast}`,
    },
    primaryButtonDisabled: {
        backgroundColor: colors.textMuted,
        cursor: 'not-allowed',
        opacity: 0.5,
        boxShadow: 'none',
    },
    backButton: {
        padding: `${spacing[2]} ${spacing[3]}`,
        border: 'none',
        background: 'none',
        color: colors.textSecondary,
        fontSize: fonts.sm,
        cursor: 'pointer',
        textDecoration: 'underline',
    },
    navRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: spacing[3],
    },
    stepIndicator: {
        textAlign: 'center' as const,
        fontSize: fonts.xs,
        color: colors.textMuted,
        marginTop: spacing[3],
    },
    disclaimer: {
        marginTop: spacing[3],
        fontSize: fonts.xs,
        color: colors.textMuted,
        textAlign: 'center' as const,
    },
    contactCardsRow: {
        display: 'flex',
        gap: spacing[4],
        flexWrap: 'wrap',
        justifyContent: 'center',
        marginTop: spacing[4],
    },
    personCard: {
        backgroundColor: colors.bgLight,
        borderRadius: radii.lg,
        border: `1px solid ${colors.border}`,
        padding: spacing[4],
        boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
        flex: '1 1 200px',
        maxWidth: 260,
    },
    personImage: {
        width: '100%',
        height: 140,
        display: 'block',
        borderRadius: radii.md,
        objectFit: 'cover' as const,
        marginBottom: spacing[3],
    },
    personName: {
        fontSize: fonts.base,
        fontWeight: fonts.semibold as number,
        color: colors.text,
        marginBottom: spacing[1],
    },
    personRole: {
        fontSize: fonts.sm,
        color: colors.textSecondary,
        marginBottom: spacing[3],
    },
    personLinks: {
        display: 'flex',
        gap: spacing[2],
        flexWrap: 'wrap',
    },
    linkButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${spacing[1]} ${spacing[3]}`,
        fontSize: fonts.xs,
        fontWeight: fonts.semibold as number,
        color: colors.white,
        backgroundColor: colors.primary,
        borderRadius: radii.full,
        textDecoration: 'none',
    },
    linkSecondaryButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${spacing[1]} ${spacing[3]}`,
        fontSize: fonts.xs,
        fontWeight: fonts.semibold as number,
        color: colors.textSecondary,
        backgroundColor: colors.bgLight,
        borderRadius: radii.full,
        textDecoration: 'none',
    },
    commentToggle: {
        background: 'none',
        border: 'none',
        padding: 0,
        fontSize: fonts.xs,
        color: colors.textMuted,
        cursor: 'pointer',
        textDecoration: 'underline',
        textUnderlineOffset: '2px',
    },
    errorText: {
        marginTop: spacing[2],
        fontSize: fonts.sm,
        color: colors.no,
        textAlign: 'center' as const,
    },
};

export default ExpertFeedback;
